/**
 * Upsell Modal Trigger
 *
 * Listens for variant:add events and opens the upsell modal.
 * Product filtering and rendering is handled entirely in Liquid.
 */

const DEBUG = window.Shopify?.designMode || false;

function debugLog(message, ...args) {
  if (DEBUG) {
    console.log(`[UpsellModal] ${message}`, ...args);
  }
}

function debugWarn(message, ...args) {
  if (DEBUG) {
    console.warn(`[UpsellModal] ${message}`, ...args);
  }
}

function debugError(message, ...args) {
  if (DEBUG) {
    console.error(`[UpsellModal] ${message}`, ...args);
  }
}

class UpsellModalTrigger extends HTMLElement {
  constructor() {
    super();
    this.modalId = this.dataset.modalId;
    this.showOnce = this.dataset.showOnce === "true";
    this.showOnCheckout = this.dataset.showOnCheckout === "true";

    if (!this.modalId) {
      debugWarn("No modal ID provided");
      return;
    }

    // Store section ID BEFORE modal is opened and moved to body
    this.sectionId = this.getSectionId();

    if (!this.sectionId) {
      debugWarn("Could not find section ID");
      return;
    }

    debugLog("Initialized", {
      modalId: this.modalId,
      sectionId: this.sectionId,
      showOnCheckout: this.showOnCheckout,
    });
    this.init();
  }

  init() {
    // Store reference for cleanup
    this.variantAddListener = (e) => this.checkAndTrigger(e);
    document.addEventListener("variant:add", this.variantAddListener);

    // Checkout click interception (if enabled)
    if (this.showOnCheckout) {
      this.setupCheckoutInterception();
    }
  }

  setupCheckoutInterception() {
    debugLog("Setting up checkout interception");

    this.checkoutClickListener = (e) => {
      // Find checkout button
      const checkoutButton = e.target.closest(
        'button[name="checkout"], a[href*="/checkouts"], [data-shopify-checkout-button]'
      );

      if (!checkoutButton) return;

      debugLog("Checkout button clicked, checking for upsells");

      // Prevent default checkout navigation
      e.preventDefault();
      e.stopPropagation();

      // Store checkout URL for later
      if (checkoutButton.tagName === "A") {
        this.checkoutUrl = checkoutButton.href;
      } else {
        this.checkoutUrl = "/checkout";
      }

      // Trigger modal check
      this.checkAndTriggerCheckout();
    };

    // Use capture phase to intercept BEFORE other handlers
    document.addEventListener("click", this.checkoutClickListener, true);
  }

  async checkAndTriggerCheckout() {
    debugLog("Checking for last chance upsells");

    // Check if modal is already open
    const modal = document.querySelector(`#${this.modalId}`);
    if (modal && modal.hasAttribute("open")) {
      debugLog("Modal already open, proceeding to checkout");
      this.proceedToCheckout();
      return;
    }

    // Session check
    const sessionKey = `upsell-modal-${this.modalId}-checkout-shown`;
    if (this.showOnce && sessionStorage.getItem(sessionKey)) {
      debugLog("Modal already shown on checkout, proceeding");
      this.proceedToCheckout();
      return;
    }

    const refreshed = await this.refreshModalContent();

    if (!refreshed) {
      debugWarn("Section rendering failed → Proceeding to checkout");
      this.proceedToCheckout();
      return;
    }

    const hasProducts = this.checkModalHasProducts();

    if (!hasProducts) {
      debugLog("No products to show → Proceeding to checkout");
      this.proceedToCheckout();
      return;
    }

    debugLog("Modal content ready, showing last chance upsell");
    this.showModalWithContext("checkout");

    // Mark as shown
    if (this.showOnce) {
      sessionStorage.setItem(sessionKey, "true");
    }

    if (modal) {
      // Setup cleanup with checkout redirect
      this.setupCheckoutCleanupHandlers(modal);
      this.startListeningForCartUpdates();
    }
  }

  setupCheckoutCleanupHandlers(modal) {
    // Clear any existing cleanup handlers to prevent stacking
    if (this.currentCheckoutCleanup) {
      debugLog("Removing previous checkout cleanup handlers");
      modal.removeEventListener("dialog:close", this.currentCheckoutCleanup);
      modal.removeEventListener(
        "dialog:force-close",
        this.currentCheckoutCleanup
      );
    }

    if (this.checkoutCleanupTimeout) {
      clearTimeout(this.checkoutCleanupTimeout);
      this.checkoutCleanupTimeout = null;
    }

    const cleanup = () => {
      debugLog("Modal closing, proceeding to checkout");
      this.stopListeningForCartUpdates();
      this.proceedToCheckout();

      modal.removeEventListener("dialog:close", cleanup);
      modal.removeEventListener("dialog:force-close", cleanup);

      if (this.checkoutCleanupTimeout) {
        clearTimeout(this.checkoutCleanupTimeout);
        this.checkoutCleanupTimeout = null;
      }

      this.currentCheckoutCleanup = null;
    };

    this.currentCheckoutCleanup = cleanup;

    modal.addEventListener("dialog:close", cleanup, { once: true });
    modal.addEventListener("dialog:force-close", cleanup, { once: true });

    // Paranoid fallback
    this.checkoutCleanupTimeout = setTimeout(() => {
      debugWarn("Checkout modal cleanup timeout - forcing checkout");
      cleanup();
    }, 5 * 60 * 1000);

    debugLog("Checkout cleanup handlers attached");
  }

  proceedToCheckout() {
    debugLog("Proceeding to checkout:", this.checkoutUrl || "/checkout");
    window.location.href = this.checkoutUrl || "/checkout";
  }

  /**
   * Get section ID from modal's original position in DOM
   * MUST be called during initialization before modal is moved to body
   */
  getSectionId() {
    const modal = document.querySelector(`#${this.modalId}`);
    if (!modal) {
      return null;
    }

    const sectionContainer = modal.closest("[data-section-id]");
    if (!sectionContainer) {
      return null;
    }

    return sectionContainer.dataset.sectionId;
  }

  disconnectedCallback() {
    debugLog("Custom element removed from DOM, cleaning up");

    // Remove main variant:add listener
    if (this.variantAddListener) {
      document.removeEventListener("variant:add", this.variantAddListener);
      this.variantAddListener = null;
    }

    // Remove checkout click listener
    if (this.checkoutClickListener) {
      document.removeEventListener("click", this.checkoutClickListener, true);
      this.checkoutClickListener = null;
    }

    // Clear checkout timeout
    if (this.checkoutCleanupTimeout) {
      clearTimeout(this.checkoutCleanupTimeout);
      this.checkoutCleanupTimeout = null;
    }

    // Clean up modal-specific listeners
    this.stopListeningForCartUpdates();
  }

  async checkAndTrigger(event) {
    debugLog("variant:add event received");

    // Ignore if modal is already open (add-to-cart from within modal)
    const modal = document.querySelector(`#${this.modalId}`);
    if (modal && modal.hasAttribute("open")) {
      debugLog("Modal already open, ignoring");
      return;
    }

    if (
      this.showOnce &&
      sessionStorage.getItem(`upsell-modal-${this.modalId}-shown`)
    ) {
      debugLog("Modal already shown in this session, ignoring");
      return;
    }

    this.closeCartDrawerImmediately();
    debugLog("Cart drawer closed, fetching modal content...");

    const refreshed = await this.refreshModalContent();

    if (!refreshed) {
      debugWarn("Section rendering failed → Opening cart drawer instead");
      this.openCartDrawer();
      return;
    }

    const hasProducts = this.checkModalHasProducts();

    if (!hasProducts) {
      debugLog("No products to show → Opening cart drawer instead");
      this.openCartDrawer();
      return;
    }

    debugLog("Modal content ready, showing modal");
    this.showModalWithContext("cart");

    if (modal) {
      this.setupCleanupHandlers(modal);
      this.startListeningForCartUpdates();
    }
  }

  setupCleanupHandlers(modal) {
    // Clear any existing cleanup handlers to prevent stacking
    if (this.currentCleanup) {
      debugLog("Removing previous cleanup handlers");
      modal.removeEventListener("dialog:close", this.currentCleanup);
      modal.removeEventListener("dialog:force-close", this.currentCleanup);
    }

    if (this.cleanupTimeout) {
      clearTimeout(this.cleanupTimeout);
      this.cleanupTimeout = null;
    }

    // Create cleanup function that removes all listeners
    const cleanup = () => {
      debugLog("Modal closing, cleaning up listeners");
      this.stopListeningForCartUpdates();
      this.openCartDrawer();

      // Remove all event listeners
      modal.removeEventListener("dialog:close", cleanup);
      modal.removeEventListener("dialog:force-close", cleanup);

      // Clear timeout
      if (this.cleanupTimeout) {
        clearTimeout(this.cleanupTimeout);
        this.cleanupTimeout = null;
      }

      // Clear reference
      this.currentCleanup = null;
    };

    // Store reference for potential removal
    this.currentCleanup = cleanup;

    // Attach to multiple close events for guaranteed cleanup
    modal.addEventListener("dialog:close", cleanup, { once: true });
    modal.addEventListener("dialog:force-close", cleanup, { once: true });

    // Paranoid fallback: Auto-cleanup after 5 minutes
    this.cleanupTimeout = setTimeout(() => {
      debugWarn(
        "Modal cleanup timeout triggered (5 minutes) - forcing cleanup"
      );
      cleanup();
    }, 5 * 60 * 1000);

    debugLog("Cleanup handlers attached");
  }

  startListeningForCartUpdates() {
    const modal = document.querySelector(`#${this.modalId}`);
    if (!modal) return;

    this.lastClickedButton = null;
    this.buttonObserver = null;

    // Block cart drawer when adding from modal
    this.variantAddHandler = (e) => {
      if (modal.contains(e.target)) {
        if (e.detail) {
          e.detail.blockCartDrawerOpening = true;
        }
      }
    };

    // Use capture phase to intercept before other handlers
    document.addEventListener("variant:add", this.variantAddHandler, true);

    this.buttonClickHandler = (e) => {
      const button = e.target.closest('button[type="submit"]');
      if (!button || !modal.contains(button)) return;

      this.lastClickedButton = button;

      if (this.buttonObserver) {
        this.buttonObserver.disconnect();
      }

      // Watch for aria-busy removal (loading finished)
      this.buttonObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.type === "attributes" &&
            mutation.attributeName === "aria-busy"
          ) {
            const ariaBusy = button.getAttribute("aria-busy");

            if (!ariaBusy || ariaBusy === "false") {
              this.updateButtonToInCartState(button);

              this.buttonObserver.disconnect();
              this.buttonObserver = null;
              this.lastClickedButton = null;
            }
          }
        });
      });

      this.buttonObserver.observe(button, {
        attributes: true,
        attributeFilter: ["aria-busy"],
      });
    };

    modal.addEventListener("click", this.buttonClickHandler);
  }

  stopListeningForCartUpdates() {
    debugLog("Stopping cart update listeners");

    if (this.buttonObserver) {
      this.buttonObserver.disconnect();
      this.buttonObserver = null;
    }

    if (this.variantAddHandler) {
      document.removeEventListener("variant:add", this.variantAddHandler, true);
      this.variantAddHandler = null;
    }

    const modal = document.querySelector(`#${this.modalId}`);
    if (modal && this.buttonClickHandler) {
      modal.removeEventListener("click", this.buttonClickHandler);
      this.buttonClickHandler = null;
    }

    // Clear any pending timeouts
    if (this.cleanupTimeout) {
      clearTimeout(this.cleanupTimeout);
      this.cleanupTimeout = null;
    }

    this.lastClickedButton = null;
  }

  updateButtonToInCartState(button) {
    if (!button) return;

    try {
      const inCartLabel = button.dataset.inCartLabel || "Im Warenkorb";
      const inCartStyle = button.dataset.inCartStyle;
      const inCartIcon = button.dataset.inCartIcon;
      const inCartBackground = button.dataset.inCartBackground;
      const inCartTextColor = button.dataset.inCartTextColor;

      const textWithIcon = button.querySelector(".text-with-icon");
      if (textWithIcon) {
        const textNode = Array.from(textWithIcon.childNodes).find(
          (node) => node.nodeType === Node.TEXT_NODE
        );
        if (textNode) {
          textNode.textContent = inCartLabel;
        }

        // Icon change: Can't swap SVGs in JS, so we keep existing or remove if "none"
        const iconElement = textWithIcon.querySelector("svg");
        if (inCartIcon === "none" && iconElement) {
          iconElement.remove();
        }
      } else {
        button.textContent = inCartLabel;
      }

      if (inCartStyle) {
        if (inCartStyle === "outline") {
          button.classList.add("button--outline");
        } else {
          button.classList.remove("button--outline");
        }
      }

      if (inCartBackground) {
        button.style.setProperty("--button-background", inCartBackground);
      }
      if (inCartTextColor) {
        button.style.setProperty("--button-text-color", inCartTextColor);
        if (inCartStyle === "outline") {
          button.style.setProperty("--button-outline-color", inCartTextColor);
        }
      }

      button.disabled = true;
      button.setAttribute("aria-label", inCartLabel);

      debugLog("Button updated to 'in cart' state");
    } catch (error) {
      debugError("Failed to update button state:", error);
      // Fallback: At least disable and show simple confirmation
      button.disabled = true;
      button.textContent = "✓ Added";
      button.setAttribute("aria-label", "Added to cart");
    }
  }

  async refreshModalContent() {
    try {
      if (!this.sectionId) {
        debugError("No section ID available");
        return false;
      }

      const url = `${window.Shopify?.routes?.root || "/"}?sections=${
        this.sectionId
      }`;
      debugLog("Fetching section:", url);

      const response = await fetch(url);

      if (!response.ok) {
        debugError(
          "Section fetch failed:",
          response.status,
          response.statusText
        );
        return false;
      }

      const data = await response.json();
      const sectionHtml = data[this.sectionId];

      if (!sectionHtml) {
        debugError("No HTML in section response");
        return false;
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHtml, "text/html");

      const newModalContent = doc.querySelector(".kc-modal__content");

      if (!newModalContent) {
        debugError("Could not find .kc-modal__content in response");
        return false;
      }

      const modal = document.querySelector(`#${this.modalId}`);
      if (!modal) {
        debugError("Modal element not found in DOM");
        return false;
      }

      const currentModalContent = modal.querySelector(".kc-modal__content");
      if (!currentModalContent) {
        debugError("Modal content container not found");
        return false;
      }

      // Replace content to re-apply Liquid filtering
      currentModalContent.innerHTML = newModalContent.innerHTML;
      debugLog("Modal content updated successfully");

      return true;
    } catch (error) {
      debugError("Exception during section fetch:", error);
      return false;
    }
  }

  checkModalHasProducts() {
    const modal = document.querySelector(`#${this.modalId}`);
    if (!modal) {
      debugWarn("Modal not found when checking for products");
      return false;
    }

    const repeater = modal.querySelector(".upsell-products-repeater");
    if (!repeater) {
      debugLog("No product repeater found");
      return false;
    }

    const productItems = repeater.querySelectorAll(".upsell-product-item");
    const hasProducts = productItems.length > 0;
    debugLog(`Found ${productItems.length} product(s) to display`);
    return hasProducts;
  }

  closeCartDrawerImmediately() {
    const cartDrawer = document.querySelector("#cart-drawer");
    if (!cartDrawer) {
      debugLog("Cart drawer not found");
      return;
    }

    if (typeof cartDrawer.hide === "function") {
      cartDrawer.hide();
    }

    cartDrawer.removeAttribute("open");
    cartDrawer.dispatchEvent(new CustomEvent("dialog:force-close"));
    debugLog("Cart drawer closed");
  }

  openCartDrawer() {
    const cartDrawer = document.querySelector("#cart-drawer");
    if (cartDrawer && typeof cartDrawer.show === "function") {
      cartDrawer.show();
      debugLog("Cart drawer opened");
    } else {
      debugWarn("Cart drawer not available");
    }
  }

  showModalWithContext(context = "cart") {
    const modal = document.querySelector(`#${this.modalId}`);
    if (!modal || typeof modal.show !== "function") {
      debugWarn(
        "Cannot show modal - element not found or missing show() method"
      );
      return;
    }

    // Set context data attribute for CSS/JS to react to
    modal.dataset.triggerContext = context;
    debugLog(`Modal context set to: ${context}`);

    modal.show();
    debugLog("Modal displayed");

    if (this.showOnce) {
      sessionStorage.setItem(`upsell-modal-${this.modalId}-shown`, "true");
      debugLog("Modal marked as shown in session");
    }
  }

  showModal() {
    // Legacy method - defaults to cart context
    this.showModalWithContext("cart");
  }

  closeModal() {
    const modal = document.querySelector(`#${this.modalId}`);
    if (modal) {
      const closeButton = modal.querySelector('button[is="close-button"]');
      if (closeButton) {
        closeButton.click();
      }
    }
  }
}

if (!customElements.get("upsell-modal-trigger")) {
  customElements.define("upsell-modal-trigger", UpsellModalTrigger);
}
